import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

import { fNumber } from 'src/utils/format/format-number';
import { formatCompactNumber } from 'src/utils/format/format-compact-number';

import { RADIUS } from 'src/theme/ui-tokens';

import { MediaShot } from 'src/components/cmui/media-shot';
import { ChannelMarkIcon } from 'src/components/cmui/channel-tag';

import { formatCostPerUnitWon } from 'src/sections/campaign/utils/campaign-list-format';

import { CM } from '../constants/home-theme';
import { HomeSceneFrame } from './home-scene-frame';
import { HomeSceneMediaGrid } from './home-scene-media-grid';
import { sceneAsset, SEEDING_POSTS, SEEDING_RESULT, SEEDING_ACCOUNTS } from '../constants/home-scene-samples';

import type { HomeScene } from '../constants/home-copy';

// ----------------------------------------------------------------------

/** 회수율 — 등록한 시딩 계정 중 올린 계정 ÷ 등록 계정(캠페인 화면과 같은 정의) */
const RATE = Math.round((SEEDING_RESULT.uploaded / SEEDING_RESULT.accounts) * 100);

const CPV = formatCostPerUnitWon(SEEDING_RESULT.costWon, SEEDING_RESULT.views) ?? '-';

// ----------------------------------------------------------------------

/**
 * 장면 5 · 시딩 성과 모니터링 — 회수율 하나를 크게, 계정마다 올렸나 아직인가, 그리고 올라온 글.
 *
 * 🔑 올라온 글은 컨텐츠 격자 카드 그대로다(`MediaGrid` 읽기 전용 · 넓은 화면만) · CPV 는 캠페인 목록과 같은 셈(`formatCostPerUnitWon`).
 * 🔑 회수율은 이 장면에서 한 번만 적는다 — 숫자 줄과 카드가 같은 값을 두 번 말하지 않는다.
 * ⚠️ 계정 줄은 예시다 — 실제 캠페인의 계정 표는 펼치면 메모·북마크를 서버에 물어서 공개 홈에 둘 수 없다.
 */
export function HomeSceneSeeding({ scene }: { scene: HomeScene }) {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <HomeSceneFrame scene={scene} tinted>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 0.5, sm: 3 }}
          alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
          sx={{ px: 2.5, py: 2, borderRadius: RADIUS.lg, border: `1px solid ${CM.border}`, bgcolor: '#fff' }}
        >
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: CM.textMuted }}>회수율</Typography>
            <Typography sx={{ fontSize: { xs: 44, md: 56 }, fontWeight: 700, lineHeight: 1.05, color: CM.primary, letterSpacing: '-0.02em' }}>
              {RATE}%
            </Typography>
          </Box>
          <Box sx={{ pb: { sm: 0.75 } }}>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: CM.textDark }}>
              시딩 계정 {fNumber(SEEDING_RESULT.accounts)}개 중 {fNumber(SEEDING_RESULT.uploaded)}개가 올렸습니다
            </Typography>
            <Typography sx={{ fontSize: 13, color: CM.textMuted }}>
              포스팅 {fNumber(SEEDING_RESULT.posts)}건 · 조회 {formatCompactNumber(SEEDING_RESULT.views, { trimPointZero: true })} · CPV {CPV}
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
          {SEEDING_ACCOUNTS.map((row) => (
            <Stack
              key={row.account}
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ minWidth: 0, px: 1.25, py: 1, borderRadius: RADIUS.md, border: `1px solid ${CM.border}`, bgcolor: '#fff' }}
            >
              {row.post ? (
                <MediaShot fill src={sceneAsset(row.post.thumb)} width={36} height={36} />
              ) : (
                <Avatar sx={{ width: 36, height: 36, bgcolor: CM.bgMuted, color: CM.textSubtle, fontSize: 14, fontWeight: 600 }}>
                  {row.account.slice(0, 1).toUpperCase()}
                </Avatar>
              )}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                  <ChannelMarkIcon channel={row.channel} />
                  <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, color: CM.textDark }}>@{row.account}</Typography>
                </Stack>
                <Chip
                  size="small"
                  label={row.post ? '업로드함' : '아직'}
                  color={row.post ? 'success' : 'default'}
                  variant={row.post ? 'filled' : 'outlined'}
                  sx={{ mt: 0.5, height: 20, fontSize: 11 }}
                />
              </Box>
            </Stack>
          ))}
        </Box>

        {/* 폰은 계정 줄의 컷으로 갈음한다 — 격자까지 서면 장면이 두 화면이 된다 */}
        {!phone && <HomeSceneMediaGrid cards={SEEDING_POSTS} perRow={4} maxRowHeight={330} />}
      </Stack>
    </HomeSceneFrame>
  );
}
